import axios from 'axios';
import { setupAxios, getCvAccessToken, UserRefreshToken } from '@carvana/core-auth';
import * as rdd from 'react-device-detect';
import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from './analytics-service';
import { AnalyticEvents } from '../constants';
import { isMyCarsReskinFixed } from '../utils/myCarsReskinExperiment';

export const CV_ACCESS_TOKEN: string = 'CVAccessToken';
export const ASPX_AUTH: string = '.ASPXAUTH';

const isLocalEnvironment: boolean = process.env.CARVANA_APP_IS_LOCAL_ENVIRONMENT === 'true' || false;

const DEFAULT_TIMEOUT = 30000;
const MAX_RETRIES = 3;
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const trackAxiosEvent = (event: string, props?: any) => {
  Analytics.trackEvent(event, props);
  datadogRum.addAction(event, props);
};

// The bucket drives the http/axios behavior, so the exposure must be recorded for every enrolled
// user before any request goes out - not on the OVP success path, which dropped the ~24% of loads
// that error and biased the experiment denominator. Every page (Garage or OVP) funnels through this
// factory, so firing here once captures first contact regardless of entry point or outcome.
let bucketExposureTracked = false;

const trackBucketExposureOnce = () => {
  if (bucketExposureTracked) {
    return;
  }
  bucketExposureTracked = true;
  trackAxiosEvent(AnalyticEvents.myCarsReskinExperiment);
};

export const resetBucketExposureForTests = () => {
  bucketExposureTracked = false;
};

function validateAuthResponse(error: any) {
  const isLoggedOut = error.response && error.response.status === 401;

  // account modal instead of redirecting home.
  const isMyCarvanaPath = window.location.pathname.indexOf('my-carvana') !== -1;

  const queryString = window.location.search;
  const parameters = new URLSearchParams(queryString);

  const isLoginModalOpen = parameters.has('openAccountModal');

  const shouldPromptLogin = isLoggedOut && !isLocalEnvironment && isMyCarvanaPath && !isLoginModalOpen;

  if (shouldPromptLogin) {
    if (queryString) {
      const pathname = `${queryString}&openAccountModal=signin`;
      window.location.search = pathname;
    } else {
      window.location.search = '?openAccountModal=signin';
    }
  }
}

// CONTROL (inventory-ltw-my-cars-reskin !== control_fixed): original behavior, no retry.
const createControlInstance = (useInterceptors = true, options = {}) => {
  let instance: any = axios.create({ timeout: DEFAULT_TIMEOUT, ...options });

  instance = setupAxios(instance, { addBearerTokenToAllRequests: true });

  if (useInterceptors) {
    instance.interceptors.request.use(
      async (config: any) => {
        const originalConfig = config;

        // add device info
        originalConfig.headers['X-CVNA-Platform'] = rdd.osName;
        originalConfig.headers['X-CVNA-AppVersion'] = rdd.osVersion;
        originalConfig.headers['X-CVNA-DeviceType'] = rdd.deviceType;
        if (rdd.isMobile) {
          originalConfig.headers['X-CVNA-DeviceId'] = rdd.mobileModel;
        } else {
          originalConfig.headers['X-CVNA-DeviceId'] = rdd.browserName;
        }

        return originalConfig;
      },
      (error: any) => {
        // eslint-disable-next-line no-console
        console.error('The My Cars axiosWithAuth has encountered an error: ', error);
        // TODO: Do something with request error
        return Promise.reject(error);
      }
    );
  }

  instance.interceptors.response.use(
    async (response: any) => response.data,
    async (error: any) => {
      const status = error?.response?.status;
      const isNetworkError = !error?.response;
      const url = error?.config?.url;
      const method = (error?.config?.method || 'get').toLowerCase();

      if (status === 401) {
        trackAxiosEvent(AnalyticEvents.myCarsReskinAuth401, { url, method, retried: false });
      }
      // control has no retry/refresh: every error is a terminal failure
      trackAxiosEvent(AnalyticEvents.myCarsReskinRequestFailed, {
        url,
        method,
        status: status ?? null,
        isNetworkError,
        transient: isNetworkError || status >= 500 || status === 429,
      });

      if (error && error.response) {
        validateAuthResponse(error);
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// CONTROL_FIXED (inventory-ltw-my-cars-reskin === control_fixed): single response interceptor that
// unwraps to .data exactly once on every path (incl. retries), refresh-and-retry once on 401 (all
// methods), and retries transient failures for idempotent GETs.
const createFixedInstance = (useInterceptors = true, options = {}) => {
  const instance: any = axios.create({ timeout: DEFAULT_TIMEOUT, ...options });

  if (useInterceptors) {
    instance.interceptors.request.use(
      (config: any) => {
        const originalConfig = config;

        const token = getCvAccessToken();
        if (token) {
          originalConfig.headers.Authorization = `Bearer ${token}`;
        }

        // add device info
        originalConfig.headers['X-CVNA-Platform'] = rdd.osName;
        originalConfig.headers['X-CVNA-AppVersion'] = rdd.osVersion;
        originalConfig.headers['X-CVNA-DeviceType'] = rdd.deviceType;
        originalConfig.headers['X-CVNA-DeviceId'] = rdd.isMobile ? rdd.mobileModel : rdd.browserName;

        // Parity with setupAxios: the test env authenticates via the credentialed cookie on the
        // cross-origin apik.* APIs, so the cookie must ride along on the request AND its 401-refresh
        // replay. Without this the fixed instance sends no usable auth and every call 401s.
        if ((globalThis as any)?.CARVANA_ENVIRONMENT === 'test') {
          originalConfig.withCredentials = true;
        }

        return originalConfig;
      },
      (error: any) => Promise.reject(error)
    );
  }

  instance.interceptors.response.use(
    (response: any) => {
      // a request that previously failed transiently and was retried has now succeeded
      if (response?.config?._retryCount > 0) {
        trackAxiosEvent(AnalyticEvents.myCarsReskinRequestRetry, {
          type: 'recovered',
          url: response.config?.url,
          method: (response.config?.method || 'get').toLowerCase(),
          attempts: response.config._retryCount,
        });
      }
      return response.data;
    },
    async (error: any) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;
      const url = originalRequest?.url;
      const method = (originalRequest?.method || 'get').toLowerCase();

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        trackAxiosEvent(AnalyticEvents.myCarsReskinAuth401, { url, method, retried: false });
        const refreshed = await UserRefreshToken();
        if (refreshed) {
          trackAxiosEvent(AnalyticEvents.myCarsReskinAuthRefresh, { type: 'success', url, method });
          const token = getCvAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return instance(originalRequest);
        }
        trackAxiosEvent(AnalyticEvents.myCarsReskinAuthRefresh, { type: 'failure', url, method });
        trackAxiosEvent(AnalyticEvents.myCarsReskinRequestFailed, {
          url,
          method,
          status: status ?? null,
          isNetworkError: !error.response,
          transient: false,
        });
        validateAuthResponse(error);
        return Promise.reject(error);
      }

      const isRetryableError = !error.response || status >= 500 || status === 429;
      if (originalRequest && IDEMPOTENT_METHODS.has(method) && isRetryableError) {
        originalRequest._retryCount = originalRequest._retryCount || 0;
        if (originalRequest._retryCount < MAX_RETRIES) {
          originalRequest._retryCount += 1;
          trackAxiosEvent(AnalyticEvents.myCarsReskinRequestRetry, {
            type: 'attempt',
            url,
            method,
            status: status ?? null,
            attempt: originalRequest._retryCount,
            isNetworkError: !error.response,
          });
          const backoff = 300 * 2 ** (originalRequest._retryCount - 1);
          await wait(backoff + Math.random() * backoff);
          return instance(originalRequest);
        }
        trackAxiosEvent(AnalyticEvents.myCarsReskinRequestRetry, {
          type: 'exhausted',
          url,
          method,
          status: status ?? null,
          attempts: originalRequest._retryCount,
        });
      }

      if (status === 401) {
        trackAxiosEvent(AnalyticEvents.myCarsReskinAuth401, {
          url,
          method,
          retried: !!originalRequest?._retry,
        });
        validateAuthResponse(error);
      }

      // terminal failure: non-retryable error, exhausted retries, non-idempotent failure, or a 401 the refresh could not save
      trackAxiosEvent(AnalyticEvents.myCarsReskinRequestFailed, {
        url,
        method,
        status: status ?? null,
        isNetworkError: !error.response,
        transient: isRetryableError,
      });
      return Promise.reject(error);
    }
  );

  return instance;
};

const AxiosWithAuthService = (useIntercepters = true, options = {}) => {
  trackBucketExposureOnce();
  return isMyCarsReskinFixed()
    ? createFixedInstance(useIntercepters, options)
    : createControlInstance(useIntercepters, options);
};

export default AxiosWithAuthService;
